/**
 * Employee Workspace — operational coaching center.
 *
 * Uses Employee Profile identity as the primary subject key whenever
 * available, with managerId retained only as a compatibility fallback
 * for legacy routes and manager-backed rows.
 *
 * Reuses the existing protected notes contract and its linked action items.
 * The component never requests coaching data unless the caller has already
 * passed the same manage_users gate used by the Employee Workspace shell.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import TextArea from '../../components/ui/TextArea';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { AuthContext } from '../../context/AuthContext';
import { useTerminology } from '../../context/TenantConfigContext';
import { ApiError, apiRequest } from '../../services/apiClient';
import * as coachingApi from '../../services/coachingApi';
import {
  EmployeeDashboardGrid,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';

const NOTE_MAX = 4000;

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function didEditNote(note) {
  if (!note?.createdAt || !note?.updatedAt) return false;
  return String(note.createdAt) !== String(note.updatedAt);
}

function noteAuthorLabel(note) {
  return note?.authorManagerName || note?.authorEmail || 'Leadership';
}

function normalizeText(value) {
  return String(value || '').trim();
}

function sortNotesNewestFirst(items) {
  return [...items].sort((left, right) => {
    const dateDifference = new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
    return dateDifference || Number(right.id || 0) - Number(left.id || 0);
  });
}

function linkedActionItems(notes) {
  const seen = new Set();
  return notes.flatMap(note => Array.isArray(note.actionItems) ? note.actionItems : [])
    .filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
}

function noteMatchesSubject(note, query) {
  const noteProfileId = Number(note?.employeeProfileId);
  const noteManagerId = Number(note?.managerId);
  if (query.employeeProfileId && noteProfileId === Number(query.employeeProfileId)) return true;
  if (query.managerId && noteManagerId === Number(query.managerId)
      && (!query.employeeProfileId || !Number.isFinite(noteProfileId) || noteProfileId <= 0)) return true;
  return false;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString();
}

function NoteComposer({
  value,
  onChange,
  onSave,
  title = 'Manager Notes',
  saving = false,
  disabled = false,
  error = ''
}) {
  const remaining = NOTE_MAX - value.length;
  return (
    <Card padding="md" shadow="sm" data-testid="manager-notes-composer">
      <header className="employee-coaching__section-head">
        <div>
          <h2 className="employee-coaching__section-title">{title}</h2>
          <p className="employee-coaching__section-copy">
            Leadership-only notes for the selected employee profile.
          </p>
        </div>
      </header>

      <label className="employee-coaching__composer-field" htmlFor="manager-note-text">
        <span className="employee-coaching__composer-label">New note</span>
        <TextArea
          id="manager-note-text"
          rows={4}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Capture context, follow-ups, or leadership notes for this employee."
          disabled={disabled || saving}
          error={Boolean(error)}
          data-testid="manager-note-text"
        />
      </label>

      <div className="employee-coaching__composer-footer">
        <span
          className={`employee-coaching__composer-count${remaining < 0 ? ' is-over' : ''}`}
          data-testid="manager-note-character-count"
        >
          {remaining} characters remaining
        </span>
        <Button
          size="sm"
          onClick={onSave}
          loading={saving}
          loadingLabel="Saving…"
          disabled={disabled || !normalizeText(value) || value.length > NOTE_MAX}
          data-testid="manager-note-save"
        >
          Save note
        </Button>
      </div>

      {error ? (
        <StatusBanner kind="error" message={error} />
      ) : null}
    </Card>
  );
}

function NoteItem({
  note,
  canEdit,
  canArchive,
  isEditing = false,
  editValue = '',
  editError = '',
  onEditChange,
  onEditStart,
  onEditCancel,
  onEditSave,
  onArchive,
  busy = false
}) {
  const edited = didEditNote(note);

  return (
    <li className="employee-coaching__note" data-testid={`manager-note-${note.id}`}>
      <div className="employee-coaching__row-meta">
        <span className="employee-coaching__row-actor">{noteAuthorLabel(note)}</span>
        <span className="employee-coaching__row-when">{formatDateTime(note.createdAt)}</span>
        {edited ? (
          <span className="employee-coaching__row-anchor" data-testid={`manager-note-edited-${note.id}`}>
            Edited {formatDateTime(note.updatedAt)}
          </span>
        ) : null}
      </div>

      {isEditing ? (
        <div className="employee-coaching__edit-block">
          <TextArea
            rows={3}
            value={editValue}
            onChange={(event) => onEditChange(event.target.value)}
            disabled={busy}
            error={Boolean(editError)}
            data-testid={`manager-note-edit-text-${note.id}`}
          />
          <div className="employee-coaching__edit-actions">
            <Button
              size="sm"
              onClick={onEditSave}
              loading={busy}
              loadingLabel="Saving…"
              disabled={!normalizeText(editValue) || editValue.length > NOTE_MAX}
              data-testid={`manager-note-edit-save-${note.id}`}
            >
              Save note
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onEditCancel}
              disabled={busy}
              data-testid={`manager-note-edit-cancel-${note.id}`}
            >
              Cancel
            </Button>
          </div>
          {editError ? <StatusBanner kind="error" message={editError} /> : null}
        </div>
      ) : (
        <p className="employee-coaching__row-body">{note.noteText || note.body}</p>
      )}

      {!isEditing && (canEdit || canArchive) ? (
        <div className="employee-coaching__note-actions">
          {canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={onEditStart}
              disabled={busy}
              data-testid={`manager-note-edit-${note.id}`}
            >
              Edit
            </Button>
          ) : null}
          {canArchive ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={onArchive}
              disabled={busy}
              data-testid={`manager-note-archive-${note.id}`}
            >
              Archive
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export default function EmployeeCoachingTab({
  employeeProfileId = null,
  managerId = null,
  managerName = null,
  canViewCoaching = false,
  attendanceEnabled = false
}) {
  const { t } = useTerminology();
  const confirm = useConfirm();
  const { user } = useContext(AuthContext) || { user: null };
  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState((employeeProfileId || managerId) ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState('');
  const [busyNoteId, setBusyNoteId] = useState(null);
  const [attendance, setAttendance] = useState({ status: 'idle', summary: null, error: '' });
  const cancelRef = useRef({ cancelled: false });

  const numericEmployeeProfileId = Number(employeeProfileId);
  const numericManagerId = Number(managerId);
  const employeeProfileIdValid = Number.isFinite(numericEmployeeProfileId) && numericEmployeeProfileId > 0;
  const managerIdValid = Number.isFinite(numericManagerId) && numericManagerId > 0;

  const noteQuery = useMemo(() => ({
    employeeProfileId: employeeProfileIdValid ? numericEmployeeProfileId : null,
    managerId: managerIdValid ? numericManagerId : null,
    limit: 100
  }), [employeeProfileIdValid, managerIdValid, numericEmployeeProfileId, numericManagerId]);

  const refetch = useCallback(async () => {
    if (!canViewCoaching) {
      setStatus('forbidden');
      setNotes([]);
      return;
    }
    if (!noteQuery.employeeProfileId && !noteQuery.managerId) {
      setStatus('idle');
      setNotes([]);
      return;
    }
    cancelRef.current.cancelled = true;
    const token = { cancelled: false };
    cancelRef.current = token;
    setStatus('loading');
    setError('');
    try {
      const notesList = await coachingApi.listNotesForSubject(noteQuery);
      if (token.cancelled) return;
      const scopedNotes = (Array.isArray(notesList) ? notesList : [])
        .filter(note => noteMatchesSubject(note, noteQuery));
      setNotes(sortNotesNewestFirst(scopedNotes));
      setStatus('loaded');
    } catch (err) {
      if (token.cancelled) return;
      if (err instanceof ApiError && err.status === 403) {
        setNotes([]);
        setStatus('forbidden');
        return;
      }
      setError(err?.message || 'Unable to load manager notes.');
      setStatus('error');
    }
  }, [canViewCoaching, noteQuery]);

  useEffect(() => {
    refetch();
    return () => {
      cancelRef.current.cancelled = true;
    };
  }, [refetch]);

  useEffect(() => {
    if (!canViewCoaching || !attendanceEnabled || !employeeProfileIdValid) {
      setAttendance({ status: 'idle', summary: null, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setAttendance({ status: 'loading', summary: null, error: '' });
    apiRequest(`/api/employees/profiles/${numericEmployeeProfileId}/attendance`, { signal: controller.signal })
      .then(data => setAttendance({ status: 'loaded', summary: data?.summary || null, error: '' }))
      .catch(err => {
        if (err?.name !== 'AbortError') {
          setAttendance({ status: 'error', summary: null, error: err?.message || 'Unable to load attendance context.' });
        }
      });
    return () => controller.abort();
  }, [attendanceEnabled, canViewCoaching, employeeProfileIdValid, numericEmployeeProfileId]);

  const handleSave = useCallback(async () => {
    const normalized = normalizeText(draft);
    if (!normalized) {
      setDraftError('Enter a note before saving.');
      return;
    }
    setSaving(true);
    setDraftError('');
    try {
      await coachingApi.createNote({
        ...noteQuery,
        visibility: 'leadership',
        body: normalized
      });
      setDraft('');
      await refetch();
    } catch (err) {
      setDraftError(err?.message || 'Unable to save note.');
    } finally {
      setSaving(false);
    }
  }, [draft, noteQuery, refetch]);

  const startEditing = useCallback((note) => {
    setEditingNoteId(note.id);
    setEditDraft(note.noteText || note.body || '');
    setEditError('');
  }, []);

  const stopEditing = useCallback(() => {
    setEditingNoteId(null);
    setEditDraft('');
    setEditError('');
  }, []);

  const handleEditSave = useCallback(async (noteId) => {
    const normalized = normalizeText(editDraft);
    if (!normalized) {
      setEditError('Enter a note before saving.');
      return;
    }
    setBusyNoteId(noteId);
    setEditError('');
    try {
      await coachingApi.updateNote(noteId, {
        body: normalized,
        visibility: 'leadership'
      });
      stopEditing();
      await refetch();
    } catch (err) {
      setEditError(err?.message || 'Unable to update note.');
    } finally {
      setBusyNoteId(null);
    }
  }, [editDraft, refetch, stopEditing]);

  const handleArchive = useCallback(async (note) => {
    const ok = await confirm(
      'Archive this manager note? It will be hidden from the default timeline but kept for history.',
      { title: 'Archive note', confirmLabel: 'Archive' }
    );
    if (!ok) return;

    setBusyNoteId(note.id);
    try {
      await coachingApi.updateNote(note.id, { isArchived: true });
      if (editingNoteId === note.id) stopEditing();
      await refetch();
    } catch (err) {
      setError(err?.message || 'Unable to archive note.');
    } finally {
      setBusyNoteId(null);
    }
  }, [confirm, editingNoteId, refetch, stopEditing]);

  if (!employeeProfileIdValid && !managerIdValid) {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-coaching-no-subject">
        <EmptyState
          icon="·"
          headline={`${t('workspace.managerNotes', 'Manager Notes')} unavailable`}
          body="This workspace does not have a valid employee identity for manager notes."
        />
      </Card>
    );
  }

  if (status === 'forbidden') {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-coaching-forbidden">
        <EmptyState
          icon="·"
          headline={`${t('workspace.managerNotes', 'Manager Notes')} access restricted`}
          body="You don't have permission to view manager notes for this employee."
        />
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-coaching-error">
        <StatusBanner kind="error" message={error} onRetry={refetch} />
      </Card>
    );
  }

  if (status === 'loading' && notes.length === 0) {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-coaching-loading">
        <LoadingSkeleton variant="paragraph" lines={4} />
      </Card>
    );
  }

  const empty = notes.length === 0;
  const viewerIsAdmin = Array.isArray(user?.permissions) && user.permissions.includes('manage_users');
  const actions = linkedActionItems(notes);
  const openActions = actions.filter(item => item.status === 'open');
  const latestNote = notes[0] || null;
  const recentCutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
  const recentCount = notes.filter(note => new Date(note.createdAt || 0).getTime() >= recentCutoff).length;
  const attendanceSummary = attendance.summary;

  return (
    <div className="employee-coaching" data-testid="employee-coaching-panel">
      <EmployeeWorkspaceSection
        title="Coaching summary"
        description="Authorized activity from the existing leadership coaching record"
      >
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Coaching records" value={notes.length} detail={notes.length ? 'Current non-archived records' : 'No coaching recorded'} />
          <EmployeeSummaryCard label="Most recent" value={latestNote ? formatDate(latestNote.createdAt) : '—'} detail={latestNote ? noteAuthorLabel(latestNote) : 'No coaching recorded'} />
          <EmployeeSummaryCard label="Recent activity" value={recentCount} detail="Records in the last 30 days" />
          <EmployeeSummaryCard label="Open follow-ups" value={openActions.length} detail="Linked action items still open" tone={openActions.length ? 'warning' : 'neutral'} />
        </EmployeeDashboardGrid>
      </EmployeeWorkspaceSection>

      <NoteComposer
        value={draft}
        onChange={setDraft}
        onSave={handleSave}
        title={t('workspace.managerNotes', 'Manager Notes')}
        saving={saving}
        error={draftError}
      />

      {error ? <StatusBanner kind="error" message={error} /> : null}

      <Card padding="md" shadow="sm" data-testid="manager-notes-timeline">
        <header className="employee-coaching__section-head">
          <div>
            <h2 className="employee-coaching__section-title">Coaching history</h2>
            <p className="employee-coaching__section-copy">
              {managerName
                ? `Leadership notes for ${managerName}, newest first.`
                : 'Leadership notes for this employee profile, newest first.'}
            </p>
          </div>
        </header>

        {empty ? (
          <EmptyState
            icon="·"
            headline={`No ${t('workspace.managerNotes', 'Manager Notes').toLowerCase()} yet`}
            body="Saved leadership notes will appear here in reverse chronological order."
          />
        ) : (
          <ul className="employee-coaching__list" data-testid="manager-notes-list">
            {notes.map((note) => {
              const canEdit = Number(note.authorUserId) === Number(user?.id) || viewerIsAdmin;
              const canArchive = canEdit;
              return (
                <NoteItem
                  key={note.id}
                  note={note}
                  canEdit={canEdit}
                  canArchive={canArchive}
                  isEditing={editingNoteId === note.id}
                  editValue={editingNoteId === note.id ? editDraft : ''}
                  editError={editingNoteId === note.id ? editError : ''}
                  onEditChange={setEditDraft}
                  onEditStart={() => startEditing(note)}
                  onEditCancel={stopEditing}
                  onEditSave={() => handleEditSave(note.id)}
                  onArchive={() => handleArchive(note)}
                  busy={busyNoteId === note.id}
                />
              );
            })}
          </ul>
        )}
      </Card>

      <EmployeeDashboardGrid columns="wide">
        <EmployeeWorkspaceSection title="Follow-up" description="Action items linked to these coaching records">
          {actions.length ? (
            <ul className="employee-coaching__follow-ups" data-testid="coaching-follow-up-list">
              {actions.map(item => (
                <li key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.ownerEmail ? `Owner: ${item.ownerEmail}` : 'Owner recorded'}</span>
                  </div>
                  <div>
                    <span className="employee-coaching__status">{item.status === 'open' ? 'Open' : item.status === 'done' ? 'Completed' : 'Skipped'}</span>
                    <span>{item.dueDate ? `Due ${formatDate(item.dueDate)}` : 'No due date'}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmployeeWorkspaceEmptyState title="No linked follow-ups" description="No action items are linked to the authorized coaching history." release="Current record" />
          )}
        </EmployeeWorkspaceSection>

        <EmployeeWorkspaceSection title="Related current employee context" description="Separate context; not recorded as a reason for coaching">
          {attendanceEnabled ? (
            attendance.status === 'loading' ? <LoadingSkeleton variant="paragraph" lines={2} />
              : attendance.status === 'error' ? <StatusBanner kind="error" message={attendance.error} />
                : attendanceSummary ? (
                  <div data-testid="coaching-attendance-context">
                    <EmployeeSummaryCard label="Attendance alert" value={attendanceSummary.alertLevel || 'No signal'} detail={`${attendanceSummary.totalIncidents30 || 0} incidents in the last 30 days`} tone={attendanceSummary.alertLevel === 'Critical' ? 'warning' : 'neutral'} />
                  </div>
                ) : <EmployeeWorkspaceEmptyState title="No attendance context available" description="No current employee attendance summary is available." release="Current record" />
          ) : <EmployeeWorkspaceEmptyState title="Attendance context unavailable" description="Attendance is not enabled for this tenant." release="Current configuration" />}
          <p className="employee-coaching__context-note">Performance and employee-level goals are not available from the current data model. No relationship to coaching is inferred.</p>
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Manager commitments" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Manager commitments are planned" description="The current coaching model does not store a distinct manager commitment." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Employee acknowledgement" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Employee acknowledgement is planned" description="The current coaching model does not store acknowledgements or signatures." />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future AI assistance" description="Future capability — no AI processing is active">
        <p className="employee-coaching__context-note">Governed summaries, recurring themes, follow-up preparation, and development recommendations are reserved for a future release. Managers remain responsible for every coaching decision.</p>
      </EmployeeWorkspaceSection>
    </div>
  );
}
